import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
// @mui
import Collapse from '@mui/material/Collapse';
// routes
import { usePathname } from 'src/routes/hook';
import { useActiveLink } from 'src/routes/hook/use-active-link';
//
import NavItem from './nav-item';

// ----------------------------------------------------------------------

export default function NavList({ data, depth, hasChild, config }) {
  const pathname = usePathname();

  const modifiedHasChild = data.path.includes('posts') ? true : hasChild;
  
  // テンプレート編集ページでの特別な処理
  const isTemplateEditPage = pathname.includes('/template/') && pathname.includes('/edit') && !pathname.includes('/layout/') && !pathname.includes('/block/');
  const isLayoutEditPage = pathname.includes('/template/layout/') && pathname.includes('/edit');
  const isBlockEditPage = pathname.includes('/template/block/') && pathname.includes('/edit');
  
  // テンプレート新規作成ページでの特別な処理
  const isTemplateCreatePage = pathname.includes('/template/new');
  const isLayoutCreatePage = pathname.includes('/template/layout/new');
  const isBlockCreatePage = pathname.includes('/template/block/new');
  
  // QA生成関連ページでの特別な処理
  const isQATaskDetailsPage = pathname.includes('/qa-generation-task/') && pathname !== data.path;
  const isQAGenerationPage = pathname.includes('/qa-generation') && !pathname.includes('/qa-generation-task');
  
  // config系ページでの特別な処理
  const isConfigPage = pathname.includes('/config');
  
  const isTemplateListPath = data.path.includes('/template/list');
  const isLayoutListPath = data.path.includes('/template/layout');
  const isBlockListPath = data.path.includes('/template/block');
  const isQATaskListPath = data.path.includes('/qa-generation-task') && !data.path.includes('/qa-generation-task/');
  
  // QA生成関連のページ（/qa-generation または /qa-generation-task）でQA生成（α版）メニューをアクティブにする
  const isQARelatedPage = pathname.includes('/qa-generation');
  const isQAMenuItem = data.path.includes('/qa-generation-task');
  
  // config系のページで設定メニューをアクティブにする
  const isConfigMenuItem = data.path.includes('/config');
  
  // 各編集ページに対応する子メニューをアクティブにする
  const isTemplateListActive = (isTemplateEditPage || isTemplateCreatePage) && isTemplateListPath;
  const isLayoutListActive = (isLayoutEditPage || isLayoutCreatePage) && isLayoutListPath;
  const isBlockListActive = (isBlockEditPage || isBlockCreatePage) && isBlockListPath;
  const isQATaskListActive = isQATaskDetailsPage && isQATaskListPath;
  const isQAMenuActive = isQARelatedPage && isQAMenuItem;
  const isConfigMenuActive = isConfigPage && isConfigMenuItem;
  
  // useActiveLinkは常に呼び出す
  const linkActive = useActiveLink(data.path, modifiedHasChild);
  const active = isTemplateListActive || isLayoutListActive || isBlockListActive || isQATaskListActive || isQAMenuActive || isConfigMenuActive || linkActive;

  const externalLink = data.path.includes('http');

  const [open, setOpen] = useState(active);

  useEffect(() => {
    if (!active) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <NavItem
        item={data}
        depth={depth}
        open={open}
        active={active}
        externalLink={externalLink}
        onClick={handleToggle}
        config={config}
      />

      {hasChild && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} config={config} />
        </Collapse>
      )}
    </>
  );
}

NavList.propTypes = {
  config: PropTypes.object,
  data: PropTypes.object,
  depth: PropTypes.number,
  hasChild: PropTypes.bool,
};

// ----------------------------------------------------------------------

function NavSubList({ data, depth, config }) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          config={config}
        />
      ))}
    </>
  );
}

NavSubList.propTypes = {
  config: PropTypes.object,
  data: PropTypes.array,
  depth: PropTypes.number,
};
